import React, { useEffect, useState, useRef } from "react";
import { Check, ChevronsUpDown, Plus, Building2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function CompanyCombobox({ value, onChange, testid = "company-combobox" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/companies", { params: { search: query } })
        .then((r) => setOptions(r.data))
        .catch(() => setOptions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  const pick = (name) => {
    onChange(name);
    setQuery("");
    setOpen(false);
  };

  const trimmed = query.trim();
  const exactExists = options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          className="w-full h-10 px-3 flex items-center justify-between rounded-md border border-input bg-background text-sm text-left"
        >
          <span className={value ? "text-slate-900" : "text-slate-400"}>
            {value || "Select or type a company"}
          </span>
          <ChevronsUpDown size={15} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" data-testid={`${testid}-panel`}>
        <div className="p-2 border-b border-slate-100">
          <Input
            ref={inputRef}
            data-testid={`${testid}-input`}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search companies..."
            className="h-9"
          />
        </div>
        <div className="max-h-60 overflow-y-auto py-1">
          {options.map((o) => (
            <button
              key={o.id || o.name}
              type="button"
              data-testid={`${testid}-option-${o.name}`}
              onClick={() => pick(o.name)}
              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 text-sm"
            >
              <Building2 size={14} className="text-slate-400 shrink-0" />
              <span className="flex-1 truncate">{o.name}</span>
              {o.industry && <span className="text-xs text-slate-400 truncate">{o.industry}</span>}
              {value === o.name && <Check size={14} className="text-emerald-600" />}
            </button>
          ))}

          {trimmed && !exactExists && (
            <button
              type="button"
              data-testid={`${testid}-create`}
              onClick={() => pick(trimmed)}
              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-emerald-50 text-sm text-emerald-700 border-t border-slate-100"
            >
              <Plus size={14} className="shrink-0" /> Add "<b>{trimmed}</b>" as a new company
            </button>
          )}

          {options.length === 0 && !trimmed && (
            <p className="px-3 py-6 text-center text-sm text-slate-400">Type to search or add a company</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
